import { OwnerLinkPropertyModal } from '@/components/features/owners/OwnerLinkPropertyModal/OwnerLinkPropertyModal'
import { OwnersContext, OwnersContextType } from '@/contexts/OwnersContext'
import { useLinkProperty } from '@/hooks/useLinkProperty'
import { useOwner } from '@/hooks/useOwner'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { ArrowLeft, Edit, Plus, Trash2Icon } from 'lucide-react'
import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import PropertyCardUI from '../../components/features/properties/PropertyCard/PropertyCardUI'

export function OwnerDetailsPage() {
  const navigate = useNavigate()
  const { handleFormOpen, filters } = useContext(
    OwnersContext
  ) as OwnersContextType
  const { data: owner, isLoading, error } = useOwner()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { unlinkPropertyAsync, isUnlinking } = useLinkProperty(filters)

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }

  if (isLoading || !owner) {
    return (
      <Container maxW="none" py={8}>
        <VStack spacing={8}>
          <HStack>
            <Skeleton height="40px" width="40px" borderRadius="md" />
            <Skeleton height="32px" width="200px" />
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} w="100%">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="120px" borderRadius="lg" />
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="none" py={8}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Erro ao carregar proprietário</AlertTitle>
            <AlertDescription>
              {error?.message || 'Proprietário não encontrado'}
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="none" py={8}>
      {/* Header */}
      <HStack justify="space-between" align="center" mb={8}>
        <HStack spacing={4}>
          <IconButton
            aria-label="Voltar"
            icon={<ArrowLeft />}
            onClick={() => navigate('/owners')}
            variant="ghost"
            size="lg"
          />
          <Box>
            <Heading size="lg" mb={1}>
              {owner.name}
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Proprietário desde{' '}
              {owner.created_at
                ? formatDate(owner.created_at)
                : 'Data não disponível'}
            </Text>
          </Box>
        </HStack>
      </HStack>

      {/* Owner Info */}
      <SimpleGrid columns={{ base: 1 }} mb={8}>
        <Card>
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Informações do Proprietário</Heading>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => handleFormOpen(owner)}
                  leftIcon={<Edit size={16} />}
                >
                  Editar
                </Button>
              </HStack>
            </Flex>
            <HStack spacing={6} justify="space-between" align="start">
              <Box className="flex flex-col gap-2">
                <Text fontSize="sm" fontWeight="bold">
                  Nome
                </Text>
                <Text fontSize="md" color="gray.900">
                  {owner.name || 'Nome não disponível'}
                </Text>
              </Box>
              <Box className="flex flex-col gap-2">
                <Text fontSize="sm" fontWeight="bold">
                  Email
                </Text>
                <Text fontSize="md" color="gray.900">
                  {owner.email || 'Email não disponível'}
                </Text>
              </Box>
              <Box className="flex flex-col gap-2">
                <Text fontSize="sm" fontWeight="bold">
                  Telefone
                </Text>
                <Text fontSize="md" color="gray.900">
                  {owner.phone || '-'}
                </Text>
              </Box>
              <Box className="flex flex-col gap-2">
                <Text fontSize="sm" fontWeight="bold">
                  Data de Cadastro
                </Text>
                <Text fontSize="md" color="gray.900">
                  {owner.created_at
                    ? formatDate(owner.created_at)
                    : 'Data não disponível'}
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>
      <Card>
        <CardBody>
          <Flex justify="space-between" align="center" mb={6}>
            <Heading size="md">Lista de propriedades</Heading>
            <Button
              leftIcon={<Icon as={Plus} />}
              colorScheme="blue"
              onClick={onOpen}
              // isDisabled={availableProperties.length === 0}
            >
              Vincular Propriedade
            </Button>
          </Flex>

          {owner.properties?.length && owner.properties.length > 0 ? (
            <SimpleGrid columns={{ base: 1 }} spacing={6} mb={6}>
              {owner.properties.map((property) => (
                <Box
                  key={property.id}
                  className="flex gap-2"
                  opacity={isUnlinking ? 0.7 : 1}
                >
                  <PropertyCardUI
                    property={property}
                    onClick={() => navigate(`/properties/${property.id}`)}
                    inLine
                  />
                  <IconButton
                    aria-label="Desvincular Propriedade"
                    icon={<Trash2Icon />}
                    size="sm"
                    isLoading={isUnlinking}
                    disabled={isUnlinking}
                    colorScheme="red"
                    onClick={async (e) => {
                      e.stopPropagation()
                      await unlinkPropertyAsync({
                        propertyId: property.id,
                      })
                    }}
                  />
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="lg" mb={2}>
                Nenhuma propriedade vinculada
              </Text>
              <Text color="gray.400" fontSize="sm" mb={4}>
                Este proprietário ainda não possui propriedades cadastradas
              </Text>
              <Button
                leftIcon={<Icon as={Plus} />}
                colorScheme="blue"
                onClick={onOpen}
                // isDisabled={availableProperties.length === 0}
              >
                Vincular Primeira Propriedade
              </Button>
            </Box>
          )}
        </CardBody>
      </Card>
      <OwnerLinkPropertyModal isOpen={isOpen} onClose={onClose} owner={owner} />
    </Container>
  )
}
